import {
  Button,
  Card,
  CardFooter,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  InputOnChangeData,
  mergeClasses,
  Option,
  SearchBox,
  Spinner,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  BookmarkAddRegular,
  CopyRegular,
  Delete20Regular,
  EyeRegular,
  OpenRegular,
} from "@fluentui/react-icons";
import {
  addFavoriteUseCasePrompt,
  addUseCasePromptUsage,
  createPrompt,
  deletePrompt,
  getCategories,
  getPrompts,
  getUseCasePrompts,
  getUseCases,
  rateUseCasePrompt,
  removeFavoriteUseCasePrompt,
} from "api";
import { Constants, ConstantValues } from "common/constants";
import { LLMType } from "models/enums/LLMType";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { IUseCase } from "models/IUseCase";
import { IPromptRating, IUseCasePrompt } from "models/IUseCasePrompt";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { copyTextToClipboard } from "utils/generalUtils";
import { v4 as uuidv4 } from "uuid";
import { AllowedRagCategory, setNewConfigForKey } from "../../redux/llmConfig";
import { setCurrentPrompt } from "../../redux/selectedPrompt";
import { setTagsForKey } from "../../redux/tags";
import { showToast } from "../../redux/toastActions";
import { setUseCases } from "../../redux/useCasesSlice";
import {
  useSharedBannerStyles,
  useSharedCardStyles,
  useSharedCloseButtonStyles,
  useSharedDialogContentStyles,
  useSharedDropdownStyles,
  useSharedEmptyStateStyles,
  useSharedFilterBarStyles,
  useSharedModalStyles,
  useSharedSearchBoxStyles,
  useSharedSectionStyles,
  useSharedTextStyles,
} from "../../theme/sharedStyles";
import { ActionDialog } from "../actionDialog/ActionDialog";
import { DialogTabs } from "../dialogTabs/DialogTabs";
import { ViewPromptDialog } from "../manageUseCasePrompts/manageUseCasePromptsCardView/viewPromptDialog/ViewPromptDialog";
import { UseCasePromptCardCompact } from "./useCasePromptCardCompact/UseCasePromptCardCompact";
import { useStyles } from "./useCasePromptsModal.styles";
import {
  IUseCasePromptsModalProps,
  IUserPrompt,
} from "./useCasePromptsModal.types";

const USER_PROMPT_USE_CASE_ID = "user-prompt";

export const UseCasePromptsModal = (
  props: IUseCasePromptsModalProps,
): JSX.Element => {
  const MINIMUM_CHARS_FOR_SEARCH: number = 4;
  const styles = useStyles();
  const sharedContentStyles = useSharedDialogContentStyles();
  const searchBoxStyles = useSharedSearchBoxStyles();
  const filterBarStyles = useSharedFilterBarStyles();
  const dropdownStyles = useSharedDropdownStyles();
  const bannerStyles = useSharedBannerStyles();
  const modalStyles = useSharedModalStyles();
  const sectionStyles = useSharedSectionStyles();
  const cardStyles = useSharedCardStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();
  const emptyStateStyles = useSharedEmptyStateStyles();
  const textStyles = useSharedTextStyles();

  const useCases: IUseCase[] = useSelector(
    (state: RootState) => state.useCasesSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const currentConversation = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const conversationKey = currentConversation?.id || ConstantValues.CHAT;

  const [selectedUseCaseId, setSelectedUseCaseId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [favoritePromptIds, setFavoritePromptIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedTab, setSelectedTab] = useState<string>("all");
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [viewPromptDialogOpen, setViewPromptDialogOpen] =
    useState<boolean>(false);
  const [selectedPromptForView, setSelectedPromptForView] =
    useState<IUseCasePrompt | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allPrompts, setAllPrompts] = useState<IUseCasePrompt[]>([]);
  const [userPrompts, setUserPrompts] = useState<IUserPrompt[]>([]);
  const [newPromptDescription, setNewPromptDescription] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [isAddingPrompt, setIsAddingPrompt] = useState<boolean>(false);
  const [isDeletingPrompt, setIsDeletingPrompt] = useState<string | null>(null);
  const [deleteConfirmPromptId, setDeleteConfirmPromptId] = useState<
    string | null
  >(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const restoreFocusToModal = useCallback(() => {
    requestAnimationFrame(() => {
      surfaceRef.current?.focus();
    });
  }, []);

  const showOnlyFavorites = selectedTab === "favorites";
  const showMyPrompts = selectedTab === "myPrompts";

  const currentLLMConfig = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[ConstantValues.CHAT] ?? {
        llmType: LLMType.CLAUDE,
        rag: false,
        citate: false,
      },
  );

  useEffect((): void => {
    const userFavoritePromptIds = new Set<string>();
    for (const prompt of allPrompts) {
      if (prompt?.favoritedBy?.includes(currentUser.id) && prompt.id)
        userFavoritePromptIds.add(prompt.id);
    }

    setFavoritePromptIds(userFavoritePromptIds);
  }, [allPrompts, currentUser]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [
          fetchedUseCases,
          fetchedCategories,
          fetchedPrompts,
          fetchedUserPrompts,
        ] = await Promise.all([
          getUseCases(),
          getCategories(false),
          getUseCasePrompts(),
          getPrompts(),
        ]);
        dispatch(setUseCases(fetchedUseCases));
        setCategories(fetchedCategories);
        setAllPrompts(Array.isArray(fetchedPrompts) ? fetchedPrompts : []);
        setUserPrompts(
          Array.isArray(fetchedUserPrompts) ? fetchedUserPrompts : [],
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (props.open) {
      fetchData();
    }
  }, [props.open, dispatch]);

  const handleUsePrompt = async (prompt: IUseCasePrompt, useCase: IUseCase) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = prompt.prompt;
    const plainText =
      tempElement.textContent ||
      tempElement.innerText ||
      ConstantValues.EMPTY_STRING;

    try {
      await addUseCasePromptUsage({
        promptId: prompt.id,
        title: prompt.title,
        useCaseId: useCase.id,
      });
    } catch (err) {
      console.warn("Failed to log prompt usage", err);
    }

    const nextLlm = (
      prompt.llmModel ??
      currentLLMConfig.llmType ??
      LLMType.CLAUDE
    ).toLowerCase();
    const nextRag =
      (!!prompt.qualsEnabled || !!prompt.proposalsEnabled) ?? false;
    const nextCitate = !!prompt.citationsEnabled;
    const nextCategories: Array<AllowedRagCategory> = [];
    if (prompt.qualsEnabled)
      nextCategories.push("Qualifications" as AllowedRagCategory);
    if (prompt.proposalsEnabled)
      nextCategories.push("Proposals" as AllowedRagCategory);

    if (
      prompt.preselectedKnowledgeComponents &&
      prompt.preselectedKnowledgeComponents.length > 0
    ) {
      const preselectedTags: ITag[] = [];
      for (const tagId of prompt.preselectedKnowledgeComponents) {
        for (const category of categories) {
          const tag = category.tags.find((t) => t.id === tagId);
          if (tag) {
            preselectedTags.push(tag);
            break;
          }
        }
      }

      if (preselectedTags.length > 0) {
        if (props.onKnowledgeComponentsSelected) {
          props.onKnowledgeComponentsSelected(preselectedTags);
        } else {
          dispatch(
            setTagsForKey({ key: conversationKey, tags: preselectedTags }),
          );
        }
      }
    }

    localStorage.removeItem(ConstantValues.DRAFT_MESSAGE);
    localStorage.removeItem(ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID);
    localStorage.removeItem(ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID);

    dispatch(
      setNewConfigForKey({
        key: ConstantValues.CHAT,
        config: {
          llmType: nextLlm,
          rag: nextRag,
          sourceRagCategories: nextCategories,
          citate: nextCitate,
        },
      }),
    );

    try {
      localStorage.setItem(
        "userLLMSettings",
        JSON.stringify({
          userId: currentUser.id,
          llmType: nextLlm,
          rag: nextRag,
          citate: nextCitate,
        }),
      );
    } catch (e) {
      console.warn("Failed to persist LLM settings", e);
    }

    dispatch(
      setCurrentPrompt({
        key: ConstantValues.CHAT,
        prompt: { id: uuidv4(), description: plainText },
      }),
    );

    dispatch(showToast("Prompt copied to chat box", "success"));
    props.onClose();
  };

  const copyToClipboard = async (prompt: IUseCasePrompt, useCaseId: string) => {
    try {
      if (!prompt?.id || !useCaseId) return;

      const useCaseUrl = `${window.location.origin}?promptId=${encodeURIComponent(prompt.id)}&useCaseId=${encodeURIComponent(useCaseId)}`;

      copyTextToClipboard(useCaseUrl);
      dispatch(showToast("Link copied to clipboard", "success"));
    } catch (err) {
      console.error("Failed to copy:", err);
      dispatch(showToast("Failed to copy link", "error"));
    }
  };

  const toggleFavorite = async (prompt: IUseCasePrompt) => {
    const promptId = prompt.id ?? ConstantValues.EMPTY_STRING;
    const isFavoritePrompt = favoritePromptIds.has(promptId);
    const favoritePromptsSet = new Set(favoritePromptIds);

    if (isFavoritePrompt) {
      favoritePromptsSet.delete(promptId);
    } else {
      favoritePromptsSet.add(promptId);
    }
    setFavoritePromptIds(favoritePromptsSet);

    setAllPrompts((prev) =>
      prev.map((p) =>
        p.id !== promptId
          ? p
          : {
              ...p,
              favoritedBy: isFavoritePrompt
                ? (p.favoritedBy ?? []).filter((id) => id !== currentUser.id)
                : [...(p.favoritedBy ?? []), currentUser.id],
            },
      ),
    );

    try {
      if (isFavoritePrompt) {
        await removeFavoriteUseCasePrompt({ promptId });
        return;
      }

      await addFavoriteUseCasePrompt({ promptId });
    } catch (e) {
      setFavoritePromptIds(new Set(favoritePromptIds));
      console.warn("Failed to toggle favorite", e);
    }
  };

  const filteredPrompts = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return allPrompts.filter((p) => {
      const matchesSearch =
        query.trim().length < MINIMUM_CHARS_FOR_SEARCH ||
        p.title.toLowerCase().includes(query) ||
        (p.description ?? ConstantValues.EMPTY_STRING)
          .toLowerCase()
          .includes(query) ||
        (p.prompt ?? ConstantValues.EMPTY_STRING).toLowerCase().includes(query);

      const matchesUseCase =
        !selectedUseCaseId ||
        (p.useCaseIds && p.useCaseIds.includes(selectedUseCaseId));

      const matchesFav =
        !showOnlyFavorites ||
        favoritePromptIds.has(p.id ?? ConstantValues.EMPTY_STRING);

      const hasContent = p.prompt.trim() !== ConstantValues.EMPTY_STRING;

      return hasContent && matchesSearch && matchesUseCase && matchesFav;
    });
  }, [
    allPrompts,
    selectedUseCaseId,
    searchQuery,
    showOnlyFavorites,
    favoritePromptIds,
  ]);

  const { favoritePairs, otherPairs } = useMemo(() => {
    const favorite: Array<{ prompt: IUseCasePrompt; useCase: IUseCase }> = [];
    const other: Array<{ prompt: IUseCasePrompt; useCase: IUseCase }> = [];

    for (const prompt of filteredPrompts) {
      let associatedUseCase: IUseCase | undefined;

      if (selectedUseCaseId) {
        associatedUseCase = useCases.find((uc) => uc.id === selectedUseCaseId);
      } else if (prompt.useCaseIds && prompt.useCaseIds.length > 0) {
        associatedUseCase = useCases.find(
          (uc) => uc.id === prompt.useCaseIds![0],
        );
      }

      if (!associatedUseCase) {
        continue;
      }

      const pair = { prompt, useCase: associatedUseCase };
      (favoritePromptIds.has(prompt.id ?? ConstantValues.EMPTY_STRING)
        ? favorite
        : other
      ).push(pair);
    }

    const sortByUseCaseThenTitle = (
      a: { prompt: IUseCasePrompt; useCase: IUseCase },
      b: { prompt: IUseCasePrompt; useCase: IUseCase },
    ) => {
      const useCaseCompare = a.useCase.title.localeCompare(b.useCase.title);
      if (useCaseCompare !== 0) return useCaseCompare;
      return a.prompt.title.localeCompare(b.prompt.title);
    };

    return {
      favoritePairs: favorite.sort(sortByUseCaseThenTitle),
      otherPairs: other.sort(sortByUseCaseThenTitle),
    };
  }, [filteredPrompts, favoritePromptIds, useCases, selectedUseCaseId]);

  const upsertUserRating = (
    ratings: IPromptRating[] | undefined,
    userId: string,
    value: number,
  ): IPromptRating[] => {
    const list = ratings ?? [];
    const withoutUser = list.filter((r) => r.userId !== userId);
    if (value === 0) {
      return withoutUser;
    }

    return [...withoutUser, { userId, value }];
  };

  const onRate = async (
    prompt: IUseCasePrompt,
    value: number,
  ): Promise<void> => {
    if (!currentUser?.id) return;
    if (!prompt?.id) return;
    if (value < 0 || value > 5) return;

    try {
      await rateUseCasePrompt({
        promptId: prompt.id,
        value,
      });

      setAllPrompts((prev) =>
        prev.map((p) =>
          p.id !== prompt.id
            ? p
            : {
                ...p,
                ratings: upsertUserRating(p.ratings, currentUser.id, value),
              },
        ),
      );
    } catch (error) {
      console.warn("Failed to rate prompt", error);
    }
  };

  const handleViewPrompt = (prompt: IUseCasePrompt) => {
    setSelectedPromptForView(prompt);
    setViewPromptDialogOpen(true);
  };

  const handleAddPrompt = async () => {
    if (!newPromptDescription.trim()) {
      return;
    }

    setIsAddingPrompt(true);

    const words = newPromptDescription.trim().split(/\s+/);
    const titleWords = words.slice(0, 5).join(" ");
    const title =
      titleWords.length > 50
        ? titleWords.substring(0, 47) + "..."
        : titleWords + (words.length > 5 ? "..." : ConstantValues.EMPTY_STRING);

    try {
      await createPrompt({
        title: title,
        description: newPromptDescription,
      });

      const updatedPrompts = await getPrompts();
      setUserPrompts(Array.isArray(updatedPrompts) ? updatedPrompts : []);

      setNewPromptDescription(ConstantValues.EMPTY_STRING);
      dispatch(showToast("Prompt saved to your collection", "success"));
    } catch (error) {
      console.error("Error creating prompt:", error);
      dispatch(showToast("Failed to save prompt", "error"));
    } finally {
      setIsAddingPrompt(false);
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    setIsDeletingPrompt(promptId);

    try {
      await deletePrompt({ promptId });

      const updatedPrompts = await getPrompts();
      setUserPrompts(Array.isArray(updatedPrompts) ? updatedPrompts : []);

      dispatch(showToast("Prompt deleted successfully", "success"));
    } catch (error) {
      console.error("Error deleting prompt:", error);
      dispatch(showToast("Failed to delete prompt", "error"));
    } finally {
      setIsDeletingPrompt(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (deleteConfirmPromptId) {
      await handleDeletePrompt(deleteConfirmPromptId);
    }
    setDeleteConfirmPromptId(null);
  };

  const displayedPrompts = showMyPrompts
    ? userPrompts
        .filter((prompt) => {
          const query = searchQuery.toLowerCase();
          if (query.trim().length < MINIMUM_CHARS_FOR_SEARCH) {
            return true;
          }
          return (
            prompt.title.toLowerCase().includes(query) ||
            (prompt.description || ConstantValues.EMPTY_STRING)
              .toLowerCase()
              .includes(query)
          );
        })
        .map((prompt) => ({
          prompt: {
            id: prompt.id,
            title: prompt.title,
            prompt: prompt.description || ConstantValues.EMPTY_STRING,
            description: prompt.description || ConstantValues.EMPTY_STRING,
            user: currentUser,
            createdAt: prompt.createdAt || Date.now(),
            updatedAt: prompt.updatedAt || Date.now(),
            qualsEnabled: false,
            proposalsEnabled: false,
            citationsEnabled: false,
            useCaseIds: [],
            favoritedBy: [],
          } as IUseCasePrompt,
          useCase: {
            id: USER_PROMPT_USE_CASE_ID,
            title: "My Prompt",
            createdAt: Date.now(),
            updatedAt: Date.now(),
            user: currentUser,
            promptSuggestions: [],
          } as IUseCase,
        }))
    : showOnlyFavorites
      ? favoritePairs
      : [...favoritePairs, ...otherPairs];

  return (
    <>
      <Dialog
        open={props.open}
        modalType="modal"
        onOpenChange={(_, d) => !d.open && props.onClose()}
      >
        <DialogSurface
          ref={surfaceRef}
          className={styles.surface}
          tabIndex={-1}
        >
          <DialogBody>
            <DialogTitle
              className={mergeClasses(
                modalStyles.modalTitle,
                styles.dialogTitle,
              )}
            >
              Use Case Prompts
            </DialogTitle>

            <DialogContent
              className={mergeClasses(
                sharedContentStyles.tabbedDialogContent,
                styles.content,
              )}
            >
              <div className={styles.stickyHeader}>
                <div className={bannerStyles.infoBanner} role="note">
                  <span>
                    Can&apos;t find the right use case prompt?{" "}
                    <a
                      href={Constants.SUGGEST_PROMPT_MAILTO}
                      className={bannerStyles.infoBannerLink}
                    >
                      Email our support team
                    </a>
                    &mdash;we&apos;ll help gather your requirements and create
                    one for you.
                  </span>
                </div>

                <DialogTabs
                  tabs={[
                    { key: "all", label: "AIMS Prompts" },
                    { key: "favorites", label: "Favorites" },
                    { key: "myPrompts", label: "My Prompts" },
                  ]}
                  activeTab={selectedTab}
                  onTabClick={(tab) => setSelectedTab(tab)}
                />

                <div className={styles.headerRow}>
                  <div className={filterBarStyles.filterRow}>
                    {!showMyPrompts && (
                      <Dropdown
                        value={
                          selectedUseCaseId
                            ? useCases.find((uc) => uc.id === selectedUseCaseId)
                                ?.title
                            : "All Use Cases"
                        }
                        onOptionSelect={(_, data) =>
                          setSelectedUseCaseId(
                            data.optionValue === "ALL"
                              ? null
                              : (data.optionValue ?? null),
                          )
                        }
                        className={dropdownStyles.dropdown}
                        listbox={{ className: dropdownStyles.listbox }}
                      >
                        <Option value="ALL">All Use Cases</Option>
                        {[
                          ...useCases.filter(
                            (uc) =>
                              uc.title !== ConstantValues.OTHER_USECASE_NAME,
                          ),
                        ]
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map((useCase) => (
                            <Option key={useCase.id} value={useCase.id}>
                              {useCase.title}
                            </Option>
                          ))}
                      </Dropdown>
                    )}

                    <div className={filterBarStyles.searchContainer}>
                      <SearchBox
                        placeholder="Search by title or description..."
                        value={searchQuery}
                        onChange={(_, data: InputOnChangeData) =>
                          setSearchQuery(
                            data.value ?? ConstantValues.EMPTY_STRING,
                          )
                        }
                        className={searchBoxStyles.searchBox}
                      />
                      <span className={searchBoxStyles.helperText}>
                        Min. 4 characters to search
                      </span>
                    </div>
                  </div>
                </div>

                {showMyPrompts && (
                  <div className={styles.myPromptsInputRow}>
                    <div className={styles.myPromptsTextareaWrapper}>
                      <Textarea
                        placeholder="Enter your prompt text here"
                        value={newPromptDescription}
                        onChange={(e, data) =>
                          setNewPromptDescription(data.value)
                        }
                        resize="vertical"
                        disabled={isAddingPrompt}
                        className={styles.myPromptsTextarea}
                      />
                    </div>
                    <Tooltip
                      content="Save prompt to your collection"
                      relationship="label"
                    >
                      <Button
                        appearance="secondary"
                        icon={
                          isAddingPrompt ? (
                            <Spinner size="tiny" />
                          ) : (
                            <BookmarkAddRegular />
                          )
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddPrompt();
                        }}
                        disabled={
                          !newPromptDescription.trim() || isAddingPrompt
                        }
                        className={styles.myPromptsSaveButton}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>

              <div
                className={mergeClasses(
                  styles.scrollableContent,
                  sharedContentStyles.scrollableContent,
                )}
              >
                {isLoading ? (
                  <div
                    className={mergeClasses(
                      emptyStateStyles.container,
                      styles.loadingContainerOverride,
                    )}
                  >
                    <Spinner size="large" label="Loading prompts..." />
                  </div>
                ) : (
                  <div className={styles.grid}>
                    {displayedPrompts.map(({ prompt, useCase }) =>
                      showMyPrompts ? (
                        <Card
                          key={prompt.id}
                          className={mergeClasses(
                            cardStyles.card,
                            styles.myPromptsCardOverride,
                          )}
                        >
                          <div className={styles.myPromptsCardContent}>
                            <div className={styles.myPromptsCardTitleWrapper}>
                              <span className={styles.myPromptsCardTitle}>
                                {prompt.title}
                              </span>
                            </div>
                            <div
                              className={mergeClasses(
                                textStyles.caption,
                                styles.myPromptsCardDescriptionOverride,
                              )}
                            >
                              {prompt.description}
                            </div>

                            <CardFooter className={styles.myPromptsCardFooter}>
                              <div className={styles.myPromptsCardFooterLeft}>
                                <Tooltip
                                  content="View Prompt Text"
                                  relationship="label"
                                >
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<EyeRegular />}
                                    aria-label="View Prompt Text"
                                    className={mergeClasses(
                                      closeButtonStyles.closeButton,
                                      styles.myPromptsIconButton,
                                    )}
                                    onClick={() => handleViewPrompt(prompt)}
                                  />
                                </Tooltip>

                                <Tooltip
                                  content="Copy prompt to clipboard"
                                  relationship="label"
                                >
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<CopyRegular />}
                                    aria-label="Copy prompt to clipboard"
                                    className={mergeClasses(
                                      closeButtonStyles.closeButton,
                                      styles.myPromptsIconButton,
                                    )}
                                    onClick={() => {
                                      copyTextToClipboard(prompt.prompt);
                                      dispatch(
                                        showToast(
                                          "Prompt copied to clipboard",
                                          "success",
                                        ),
                                      );
                                    }}
                                  />
                                </Tooltip>

                                <Tooltip
                                  content="Delete prompt"
                                  relationship="label"
                                >
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={
                                      isDeletingPrompt === prompt.id ? (
                                        <Spinner size="tiny" />
                                      ) : (
                                        <Delete20Regular />
                                      )
                                    }
                                    onClick={() =>
                                      prompt.id &&
                                      setDeleteConfirmPromptId(prompt.id)
                                    }
                                    disabled={isDeletingPrompt === prompt.id}
                                    aria-label="Delete prompt"
                                    className={mergeClasses(
                                      closeButtonStyles.closeButton,
                                      styles.myPromptsIconButton,
                                      styles.deleteButton,
                                    )}
                                  />
                                </Tooltip>
                              </div>

                              <Tooltip
                                content="Use this prompt in current chat"
                                relationship="label"
                              >
                                <Button
                                  size="small"
                                  appearance="subtle"
                                  icon={<OpenRegular />}
                                  aria-label="Use this prompt in current chat"
                                  className={mergeClasses(
                                    closeButtonStyles.closeButton,
                                    styles.myPromptsIconButton,
                                  )}
                                  onClick={() =>
                                    handleUsePrompt(prompt, useCase)
                                  }
                                />
                              </Tooltip>
                            </CardFooter>
                          </div>
                        </Card>
                      ) : (
                        <UseCasePromptCardCompact
                          key={prompt.id}
                          prompt={prompt}
                          useCase={useCase}
                          isFavorite={favoritePromptIds.has(
                            prompt.id ?? ConstantValues.EMPTY_STRING,
                          )}
                          onToggleFavorite={toggleFavorite}
                          onUsePrompt={handleUsePrompt}
                          onRate={onRate}
                          onCopyToClipboard={copyToClipboard}
                          onViewPrompt={handleViewPrompt}
                        />
                      ),
                    )}

                    {displayedPrompts.length === 0 && (
                      <div
                        className={mergeClasses(
                          sectionStyles.emptyState,
                          styles.emptyStateGrid,
                        )}
                      >
                        {showOnlyFavorites
                          ? "You have no favourite prompts yet."
                          : "No prompts match your filters."}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>

            <DialogActions>
              <Button appearance="secondary" onClick={props.onClose}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ViewPromptDialog
        open={viewPromptDialogOpen}
        title={selectedPromptForView?.title ?? ConstantValues.EMPTY_STRING}
        promptHtml={
          selectedPromptForView?.prompt ?? ConstantValues.EMPTY_STRING
        }
        preselectedKnowledgeComponents={
          selectedPromptForView?.preselectedKnowledgeComponents
        }
        onClose={() => {
          setViewPromptDialogOpen(false);
          restoreFocusToModal();
        }}
      />

      {deleteConfirmPromptId !== null && (
        <ActionDialog
          isVisible={true}
          title="Delete Prompt"
          message="Are you sure you want to delete this prompt? This action cannot be undone."
          confirmationMessage="Delete"
          dismissMessage="Cancel"
          onConfirmation={async () => {
            await handleDeleteConfirmed();
            restoreFocusToModal();
          }}
          onDismiss={() => {
            setDeleteConfirmPromptId(null);
            restoreFocusToModal();
          }}
        />
      )}
    </>
  );
};
